import { useState } from 'react';

interface MaybachPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MaybachPopup({ isOpen, onClose }: MaybachPopupProps) {
  const [showScheduler, setShowScheduler] = useState(false);

  if (!isOpen) return null;

  const handleFindYourMaybach = () => {
    setShowScheduler(true);
  };

  const handleCloseScheduler = () => {
    setShowScheduler(false);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
      <div className="min-h-screen px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Mercedes-<span className="text-amber-400">Maybach</span>
            </h2>
            <button
              onClick={onClose}
              className="w-12 h-12 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-full transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-2xl"></i>
            </button>
          </div>

          <div className="bg-zinc-800 rounded-2xl overflow-hidden shadow-2xl mb-8">
            <div className="relative h-96 w-full overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/b49cd498253926d9d7e9db907dcadd74.jpg"
                alt="Maybach Collection"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent"></div>
            </div>
            
            <div className="p-8 md:p-12">
              <div className="mb-8">
                <h3 className="text-3xl font-bold text-white mb-4">
                  The Pinnacle of Mercedes-Benz Luxury
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Experience the pinnacle of Mercedes-Benz luxury with our bespoke Maybach inventory. Each Maybach represents the ultimate expression of automotive excellence, combining unparalleled craftsmanship with cutting-edge technology.
                </p>
                <p className="text-gray-300 text-lg leading-relaxed">
                  From the majestic S-Class sedan to the commanding GLS SUV, our Maybach collection offers exclusive access to vehicles that redefine luxury. Every detail is meticulously crafted, every surface exquisitely finished, and every journey transformed into an extraordinary experience.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-vip-crown-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Bespoke Luxury</h4>
                  <p className="text-gray-400 text-sm">Handcrafted interiors with exclusive materials</p>
                </div>
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-magic-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Ultimate Comfort</h4>
                  <p className="text-gray-400 text-sm">Executive rear seating with massage and climate control</p>
                </div>
                <div className="bg-zinc-900 p-6 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-lg mb-4">
                    <i className="ri-sound-module-line text-amber-400 text-2xl"></i>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-2">Burmester 4D Sound</h4>
                  <p className="text-gray-400 text-sm">Immersive audio experience with concert hall acoustics</p>
                </div>
              </div>

              <div className="bg-zinc-900 rounded-lg p-6 mb-8">
                <h4 className="text-white font-bold text-lg mb-3">Maybach Features</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-amber-400 text-xl mt-1"></i>
                    <p className="text-gray-300 text-sm">Extended wheelbase for maximum rear legroom</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-amber-400 text-xl mt-1"></i>
                    <p className="text-gray-300 text-sm">Nappa leather with diamond quilting</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-amber-400 text-xl mt-1"></i>
                    <p className="text-gray-300 text-sm">Executive rear seat package with recline</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-amber-400 text-xl mt-1"></i>
                    <p className="text-gray-300 text-sm">Champagne flutes and refrigerator</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-amber-400 text-xl mt-1"></i>
                    <p className="text-gray-300 text-sm">Magic Sky Control panoramic roof</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-check-line text-amber-400 text-xl mt-1"></i>
                    <p className="text-gray-300 text-sm">Exclusive Maybach badging and wheels</p>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleFindYourMaybach}
                className="w-full bg-amber-400 text-black font-bold py-4 rounded-lg hover:bg-amber-500 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Find your Next Maybach!
              </button>
            </div>
          </div>
        </div>
      </div>

      {showScheduler && (
        <div className="fixed inset-0 bg-black/95 z-[60] overflow-y-auto">
          <div className="min-h-screen px-6 py-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-white">
                  Find Your <span className="text-amber-400">Maybach</span>
                </h2>
                <button
                  onClick={handleCloseScheduler}
                  className="w-12 h-12 flex items-center justify-center bg-zinc-800 hover:bg-zinc-700 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-white text-2xl"></i>
                </button>
              </div>

              <div className="bg-zinc-900 rounded-lg shadow-2xl overflow-hidden">
                <div className="aspect-[16/9] w-full">
                  <iframe
                    src="https://mercedeswithjacob.square.site/"
                    className="w-full h-full"
                    style={{ minHeight: '600px' }}
                    title="Find Your Maybach"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
